import React, {useEffect, useRef, useState} from 'react';
import {
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  Dimensions,
  TextInput
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {fontFamily} from '../../helpers/fontFamily';
import useEnterKeySubmit from '../hooks/useEnterKeySubmit';

const ConfirmationDialog = props => {
  const windowWidth = Dimensions.get('window').width;

  const {
    title,
    message,

    confirmText = 'Yes, Confirm',
    cancelText = 'No, Cancel',

    onConfirm,
    onCancel,
    onClose,

    showCloseIcon = true,
    showButtons = true,
    showInput = false,
    inputDefaultValue = '',
    inputPlaceholder = 'Type here...',
    onSubmit,

    testID = 'ReusableConfirmationDialog',
    confirmTestID = 'ConfirmButton',
    cancelTestID = 'CancelButton',
    closeTestID = 'CloseIcon',
    inputTestID = 'DialogTextInput'
  } = props;

  const [inputValue, setInputValue] = useState(inputDefaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (showInput && inputRef.current) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [showInput]);

  const hasTitle = title?.trim()?.length > 0;
  const hasMessage = message?.trim()?.length > 0;

  const handleSubmit = () => {
      onSubmit(inputValue);
      setInputValue('');
      inputRef.current?.focus();
  };

  const { onKeyPress, onSubmitEditing } = useEnterKeySubmit(handleSubmit);

  return (
    <View testID={testID} style={styles.overlay}>
      <View
        style={[
          windowWidth >= 900 ? styles.desktopContainer : styles.mobileContainer,
          {marginBottom: '9%'}
        ]}>
        <LinearGradient
          locations={[0, 1]}
          start={{x: 0, y: 1}}
          end={{x: 0, y: 0}}
          colors={['#d47d02', '#ffb636']}
          style={[
            styles.dialogBox,
            {width: windowWidth >= 900 ? '70%' : '100%'}
          ]}>
          {showCloseIcon && (
            <View style={styles.closeIconContainer}>
              <TouchableOpacity
                testID={closeTestID}
                onPress={onClose || onCancel}
                style={styles.closeIcon}>
                <Text style={styles.closeIconText}>×</Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.contentContainer}>
            {hasTitle && (
              <Text
                style={[
                  styles.title,
                  {fontSize: windowWidth >= 900 ? 22 : 18}
                ]}>
                {title}
              </Text>
            )}

            {hasMessage && (
              <Text
                style={[
                  styles.message,
                  {fontSize: windowWidth >= 900 ? 18 : 16}
                ]}>
                {message}
              </Text>
            )}

            {showInput && (
              <TextInput
                ref={inputRef}
                testID={inputTestID}
                style={styles.textInput}
                placeholder={inputPlaceholder}
                value={inputValue}
                onChangeText={setInputValue}
                autoCorrect={false}
                autoCapitalize="none"
                returnKeyType="done"
                onSubmitEditing={onSubmitEditing}
                onKeyPress={onKeyPress}
                blurOnSubmit
              />
            )}
          </View>

          {showButtons && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                testID={cancelTestID}
                onPress={onCancel}
                style={styles.button}>
                <Text style={styles.buttonText}>{cancelText}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                testID={confirmTestID}
                onPress={onConfirm}
                style={styles.button}>
                <Text style={styles.buttonText}>{confirmText}</Text>
              </TouchableOpacity>
            </View>
          )}
        </LinearGradient>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  desktopContainer: {width: '50%', alignItems: 'center'},
  mobileContainer: {width: '90%', alignItems: 'center'},
  dialogBox: {
    backgroundColor: '#d3dfea',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderRadius: 12,
    borderColor: '#e8971a',
    borderWidth: 3,
    shadowColor: '#363636',
    shadowOffset: {width: 2, height: 2},
    shadowOpacity: 0.5,
    shadowRadius: 10,
    elevation: 10,
    minHeight: 200
  },
  closeIconContainer: {alignSelf: 'flex-end', marginTop: -10, marginRight: -10},
  closeIcon: {padding: 5},
  closeIconText: {fontSize: 28, fontWeight: 'bold', color: '#4b4132', fontFamily: fontFamily.font600},
  contentContainer: {alignItems: 'center', marginVertical: 20, width: '100%'},
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 15,
    color: '#4b4132',
    fontFamily: fontFamily.font600
  },
  message: {textAlign: 'center', lineHeight: 24, color: '#4b4132', fontFamily: fontFamily.font300},
  textInput: {
    width: '100%',
    marginTop: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#8B4513',
    backgroundColor: 'rgba(255,255,255,0.9)',
    color: '#4b4132',
    fontSize: 16,
    fontFamily: fontFamily.font300
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginTop: 20,
    width: '100%'
  },
  button: {
    paddingHorizontal: 25,
    paddingVertical: 12,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderWidth: 2,
    borderColor: '#8B4513',
    minWidth: 120,
    alignItems: 'center'
  },
  buttonText: {fontSize: 16, fontWeight: '600', color: '#4b4132', fontFamily: fontFamily.font600}
});

export default ConfirmationDialog;
